import { useCurrentWorkspace, Workspace } from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import { t } from 'i18next';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { DataTable, Text } from 'react-native-paper';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { ExportButton } from './dataExport/exportButton';
import { useAccountsExport } from './dataExport/useAccountsExport';
import { useActivityExport } from './dataExport/useActivityExport';
import { useAssetExport } from './dataExport/useAssetExport';
import { useTagScanExport } from './dataExport/useTagScanExport';

const ExportCell = memo(({ workspace }: { workspace: Workspace }) => (
  <View style={styles.export}>
    <ExportButton
      icon="bell-outline"
      useExport={useActivityExport}
      fileName="activity"
      workspace={workspace}
    />
    <ExportButton
      icon="tractor"
      useExport={useAssetExport}
      fileName="assets"
      workspace={workspace}
    />
    <ExportButton
      icon="account-outline"
      useExport={useAccountsExport}
      fileName="accounts"
      workspace={workspace}
      label={t('People')}
    />
    <ExportButton
      icon="qrcode"
      useExport={useTagScanExport}
      fileName="tag-scan"
      workspace={workspace}
    />
  </View>
));

export const WorkspacesSection = () => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();

  const { containerMaxHeight } = useMaxHeight();
  if (!currentWorkspace) return null;

  return (
    <View>
      <View style={styles.infoHeaderContainer}>
        <Text style={styles.infoHeader}>{t('accountPage.workplaceTitle')}</Text>
      </View>
      <View
        style={[
          styles.workspacesContainer,
          {
            maxHeight: containerMaxHeight,
          },
        ]}
      >
        <DataTable>
          <DataTable.Header style={styles.tableHeader}>
            <DataTable.Title>
              <Text style={styles.titleText}>
                {t('accountPage.table.headers.accountType')}
              </Text>
            </DataTable.Title>
            <DataTable.Title>
              <Text style={styles.titleText}>
                {t('accountPage.downloadData')}
              </Text>
            </DataTable.Title>
          </DataTable.Header>
          <DataTable.Row style={styles.tableRow}>
            <DataTable.Cell>
              <Text style={styles.workspaceName}>{currentWorkspace.name}</Text>
            </DataTable.Cell>
            <DataTable.Cell style={styles.tableCell}>
              <ExportCell workspace={currentWorkspace} />
            </DataTable.Cell>
          </DataTable.Row>
        </DataTable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  workspacesContainer: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  infoHeaderContainer: {
    flexDirection: 'column',
    alignSelf: 'flex-start',
    marginBottom: 16,
    width: '100%',
  },
  columnName: {
    fontWeight: '500',
    fontSize: 14,
  },
  export: {
    flexDirection: 'row',
    margin: 5,
    columnGap: 12,
  },
  infoHeader: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: -0.25,
  },
  tableHeader: {
    backgroundColor: Colors.primaryGrayHeader,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderBottomWidth: 0,
    paddingLeft: 0,
    paddingTop: 0,
    marginTop: 0,
    height: 'auto',
    paddingRight: 31,
  },
  tableRow: {
    cursor: 'auto',
    backgroundColor: Colors.white,
  },
  tableCell: {
    borderLeftWidth: 1,
    borderLeftColor: Colors.primaryGrayHeader,
    paddingLeft: 12,
  },
  titleText: {
    fontWeight: '500',
    fontSize: 16,
    color: Colors.blackText,
    letterSpacing: -0.2,
    paddingLeft: 20,
  },
  workspaceName: {
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
  },
});
